import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Select, useListCollection, Field} from '@chakra-ui/react3'
import {CheckIcon, ChevronDownIcon, InformationCircleIcon} from '../icons'

/**
 * Dropdown component built on Chakra UI v3 Select.
 * Provides a single-select dropdown with keyboard navigation and accessibility.
 *
 * @component
 * @param {Object} props
 * @param {Array<{label: string, value: string}>} props.items - Array of options to display
 * @param {string} [props.value] - Currently selected value (controlled)
 * @param {function} [props.onValueChange] - Callback when selection changes, receives {value, items}
 * @param {string} [props.placeholder] - Placeholder text for the trigger
 * @param {string} [props.label] - Accessible label for the dropdown
 * @param {string} [props.variant] - Visual variant: 'default' | 'filled'
 * @param {boolean} [props.disabled] - Whether the dropdown is disabled
 * @param {boolean} [props.invalid] - Whether the dropdown is in an error state
 * @param {string} [props.errorText] - Error message shown below the dropdown when invalid
 * @param {string} [props.helperText] - Info text shown below the dropdown when not invalid
 */
const DropdownComponent = ({
    items = [],
    value,
    onValueChange,
    placeholder = '',
    label,
    ariaLabel,
    variant = 'default',
    disabled = false,
    invalid = false,
    errorText,
    helperText,
    emptyText,
    ...rest
}) => {
    const [open, setOpen] = useState(false)
    const {collection} = useListCollection({
        initialItems: items
    })
    const emptyItem = {
        label: emptyText,
        value: '__empty__',
        disabled: true
    }

    return (
        <Field.Root invalid={invalid}>
            <Select.Root
                collection={collection}
                value={value != null ? [String(value)] : []}
                onValueChange={onValueChange}
                open={open}
                onOpenChange={({open: o}) => setOpen(o)}
                disabled={disabled}
                invalid={invalid}
                variant={variant}
                positioning={{
                    sameWidth: true,
                    placement: 'bottom-start',
                    offset: {mainAxis: 0}
                }}
                {...rest}
            >
                <Select.HiddenSelect />
                {label ? (
                    <Field.Label>{label}</Field.Label>
                ) : ariaLabel || rest['aria-label'] ? (
                    <Field.Label className="sr-only">{ariaLabel || rest['aria-label']}</Field.Label>
                ) : null}
                <Select.Control
                    onKeyDown={(e) => {
                        if (e.key === 'End' && !open) setOpen(true)
                    }}
                >
                    <Select.Trigger aria-label={ariaLabel || label}>
                        <Select.ValueText placeholder={placeholder} />
                    </Select.Trigger>
                    <Select.IndicatorGroup>
                        <Select.Indicator>
                            <ChevronDownIcon />
                        </Select.Indicator>
                    </Select.IndicatorGroup>
                </Select.Control>

                <Select.Positioner>
                    <Select.Content>
                        {collection.items.length === 0 && (
                            <Select.Item item={emptyItem} key={emptyItem.value}>
                                <Select.ItemText>{emptyItem.label}</Select.ItemText>
                            </Select.Item>
                        )}
                        {collection.items.map((item) => (
                            <Select.Item item={item} key={item.value}>
                                <Select.ItemText>{item.label}</Select.ItemText>
                                <Select.ItemIndicator>
                                    <CheckIcon />
                                </Select.ItemIndicator>
                            </Select.Item>
                        ))}
                    </Select.Content>
                </Select.Positioner>
            </Select.Root>

            {invalid && errorText && (
                <Field.ErrorText>
                    <InformationCircleIcon />
                    {errorText}
                </Field.ErrorText>
            )}
            {!invalid && helperText && (
                <Field.HelperText>
                    <InformationCircleIcon />
                    {helperText}
                </Field.HelperText>
            )}
        </Field.Root>
    )
}

DropdownComponent.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired
        })
    ),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onValueChange: PropTypes.func,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    ariaLabel: PropTypes.string,
    variant: PropTypes.oneOf(['default', 'filled']),
    disabled: PropTypes.bool,
    invalid: PropTypes.bool,
    errorText: PropTypes.string,
    helperText: PropTypes.string,
    emptyText: PropTypes.string
}

export default DropdownComponent
